import { Form, Formik } from "formik";
import { Box, Grid, InputAdornment, Typography } from "@material-ui/core";
import FormAutoComplete, {
  FormSelect,
} from "../../../forms/formik/FormSelectField";
import FormTextInput from "../../../forms/formik/FormTextInput";

import CustomButton from "../../../forms/CustomButton";
import SubmitButton from "../../../forms/formik/FormSubmitButton";
import { convertToInr } from "../../../../utils/cart";
import useStp, { Values } from "./useStp";
import { FormDateInput } from "../../../forms/formik/FormDateField";
import GridContainer from "../../../../lib/GridContainer";
import { FormRadio } from "../../../forms/formik/FormRadioField";
import { AddToCartStatusModal } from "../../customModal";

const EDELWEISSFUND_CODE = "118";

export enum Frequency {
  D = "DAILY",
  W = "WEEKLY",
  F = "FORTNIGHTLY",
  M = "MONTHLY",
  Q = "QUARTERLY",
  H = "HALFYEARLY",
  Y = "YEARLY",
}

export const STP = () => {
  const {
    initialState,
    validationSchema,
    onSubmit,
    addToCart,
    changeHandlers,
    options,
    dateProps,
    modalDetails,
    history,
    pan,
  } = useStp();

  return (
    <Grid container>
      <AddToCartStatusModal
        message={`Transaction has been added to cart successfully with the reference number ${modalDetails.ihNo} and bulk-order ID ${modalDetails.bulkOrderId}`}
        open={modalDetails.isOpen}
        image={"/images/cart_success.svg"}
        btn_one={"Go to cart"}
        btn_two={"Add more"}
        history={history}
        pan={pan}
        onAddTransaction={() => {
          history.push(`/cart/add-transaction/stp`, {
            pan,
          });
        }}
      />

      <Grid item lg={9} sm={12}>
        <Formik<Values>
          initialValues={initialState}
          validationSchema={validationSchema}
          onSubmit={addToCart}
          validateOnBlur
          enableReinitialize
        >
          {({ values, setValues, ...rest }) => (
            <Form>
              <Typography
                component="h4"
                style={{
                  fontWeight: "800",
                  fontSize: "24px",
                  marginBottom: "20px",
                  fontFamily: "OpenSans-SemiBold",
                }}
              >
                STP
              </Typography>
              <FormAutoComplete
                name="amc"
                label={"AMC"}
                options={options.fundOptions}
                onChange={changeHandlers.amc(values, setValues)}
              />
              <FormSelect
                name="folio"
                label="Folio"
                disabled={!values.amc}
                options={options.folioOptions}
                onChange={changeHandlers.folio(values, setValues)}
              />
              <FormAutoComplete
                name="switchOutScheme"
                label="Transfer From Scheme"
                disabled={!values.folio}
                options={options.switchOutOptions}
                onChange={changeHandlers.switchOut(values, setValues)}
              />

              <FormAutoComplete
                name="category"
                label="Category"
                disabled={!values.switchOutScheme}
                options={options.categoryOptions}
                onChange={changeHandlers.category(values, setValues)}
              />
              <FormAutoComplete
                name="switchInScheme"
                label="Transfer To Scheme"
                disabled={!values.category}
                options={options.switchInOptions}
                onChange={changeHandlers.switchIn(values, setValues)}
              />

              <FormAutoComplete
                name="stpFrequency"
                label="STP Frequency"
                options={options.frequencyOptions}
                disabled={!values.switchInScheme}
                onChange={changeHandlers.frequency(values, setValues)}
              />

              <Typography>Time Period</Typography>

              <GridContainer>
                <Box display="flex" flexDirection="column">
                  <FormDateInput
                    name="stpStartDate"
                    label="STP Start Date"
                    {...dateProps.startDate}
                    disablePast
                    onChange={changeHandlers.handleStartDateChange(values, {
                      ...rest,
                      setValues,
                    })}
                    InputProps={{ readOnly: false }}
                    shouldDisableDate={
                      values.amc === EDELWEISSFUND_CODE &&
                      values.stpFrequency === Frequency.W
                        ? () => false
                        : disableDate
                    }
                  />
                  {values.minimumInstallments ? (
                    <Typography
                      variant="caption"
                      style={{
                        marginTop: "1rem",
                        fontWeight: "bolder",
                        color: "rgb(0, 61, 120)",
                      }}
                    >
                      Minimum Installments :{" "}
                      <strong>{values.minimumInstallments}</strong>
                    </Typography>
                  ) : null}
                </Box>

                <Box display="flex" flexDirection="column">
                  <FormDateInput
                    name="stpEndDate"
                    label="STP End Date"
                    disablePast
                    onChange={changeHandlers.handleEndDateChange(values, {
                      ...rest,
                      setValues,
                    })}
                    {...dateProps.endDate}
                    InputProps={{ readOnly: false }}
                    disabled={!values.stpStartDate}
                    shouldDisableDate={
                      values.stpFrequency === Frequency.D
                        ? disableDate
                        : () => false
                    }
                  />
                </Box>
              </GridContainer>

              <GridContainer>
                <FormTextInput name="arnCode" label="ARN Code" disabled />
                <FormTextInput
                  name="subArn"
                  label="Sub ARN Code"
                  onBlur={changeHandlers.handleSubArnChange(values, setValues)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">ARN -</InputAdornment>
                    ),
                  }}
                />
              </GridContainer>
              <GridContainer>
                <FormTextInput name="subBrokerCode" label="Sub-Broker Code" />
              </GridContainer>
              <FormRadio
                name="euinDeclaration"
                label="EUIN"
                options={[
                  {
                    label: "Yes",
                    value: "Yes",
                  },
                  {
                    label: "No",
                    value: "No",
                  },
                ]}
              />
              {values.euinDeclaration === "Yes" ? (
                <FormAutoComplete
                  name="euin"
                  label="EUIN Code"
                  options={options.euinOptions}
                />
              ) : (
                <Typography
                  style={{
                    color: "rgba(128, 128, 128, 0.7)",
                  }}
                >
                  I/We hereby confirm that the EUIN box has been intentionally
                  left the blank by me/us as this is an 'execution-only'
                  transaction without any interaction or advice by any personnel
                  of the above distributor or notwithstanding the advice of
                  in-appropriateness,if any provided by any personnel of the
                  distributor and the distributor has not charged any advisory
                  fees on this transaction
                </Typography>
              )}
              <FormAutoComplete
                name="stpOption"
                label="STP Option"
                options={options.stpOptions}
              />

              {values.stpOption === "F" && (
                <FormTextInput
                  name="amount"
                  label={
                    `Investment Amount ` +
                    (!!values.minAmount
                      ? `(Minimum ${convertToInr(values.minAmount)})`
                      : "")
                  }
                  type="number"
                />
              )}

              <Box display="flex">
                <CustomButton
                  label="Submit"
                  variant="outlined"
                  onClick={() => onSubmit(values, rest)}
                />

                <SubmitButton label={"Add To Cart"} variant="contained" />
              </Box>
            </Form>
          )}
        </Formik>
      </Grid>
    </Grid>
  );
};

const disableDate = (date: Date) =>
  [0, 6].includes(date.getDay()) || date.getDate() > 28;
